import { getOpenCV } from "./opencv";
import { debugLog } from "../../../utils/debug";

// Semi-dense candidate detection
export async function detectSemiDenseCandidates(
  imageElement,
  { maxCorners = 4000, qualityLevel = 0.005, minDistance = 5 } = {},
) {
  const cv = await getOpenCV();
  const source = cv.imread(imageElement);
  const gray = new cv.Mat();

  let detector = null;
  let keypoints = null;

  try {
    cv.cvtColor(source, gray, cv.COLOR_RGBA2GRAY);
    detector = new cv.GFTTDetector(maxCorners, qualityLevel, minDistance);
    keypoints = new cv.KeyPointVector();
    detector.detect(gray, keypoints);

    const points = [];

    for (let i = 0; i < keypoints.size(); i += 1) {
      const keypoint = keypoints.get(i);

      points.push({
        x: keypoint.pt.x,
        y: keypoint.pt.y,
        response: keypoint.response,
      });
    }

    debugLog("[Reconstruction] Semi-dense candidates:", {
      detected: points.length,
    });

    return points;
  } finally {
    if (keypoints) {
      keypoints.delete();
    }

    if (detector) {
      detector.delete();
    }

    gray.delete();
    source.delete();
  }
}

// Epipolar lines
export function computeEpipolarLine(F, pointA) {
  const x = pointA.x;
  const y = pointA.y;
  const a = F.get(0, 0) * x + F.get(0, 1) * y + F.get(0, 2);
  const b = F.get(1, 0) * x + F.get(1, 1) * y + F.get(1, 2);
  const c = F.get(2, 0) * x + F.get(2, 1) * y + F.get(2, 2);
  const length = Math.sqrt(a * a + b * b);

  if (length < 1e-12) {
    return null;
  }

  return {
    a: a / length,
    b: b / length,
    c: c / length,
  };
}

export function computeReverseEpipolarLine(F, pointB) {
  const x = pointB.x;
  const y = pointB.y;
  const a = F.get(0, 0) * x + F.get(1, 0) * y + F.get(2, 0);
  const b = F.get(0, 1) * x + F.get(1, 1) * y + F.get(2, 1);
  const c = F.get(0, 2) * x + F.get(1, 2) * y + F.get(2, 2);
  const length = Math.sqrt(a * a + b * b);

  if (length < 1e-12) {
    return null;
  }

  return {
    a: a / length,
    b: b / length,
    c: c / length,
  };
}

// Line clipping
export function clipEpipolarLineToImage(line, width, height) {
  if (!line || width <= 0 || height <= 0) {
    return null;
  }

  const { a, b, c } = line;
  const intersections = [];

  const addPoint = (x, y) => {
    const epsilon = 1e-6;

    if (
      x >= -epsilon &&
      x <= width - 1 + epsilon &&
      y >= -epsilon &&
      y <= height - 1 + epsilon
    ) {
      const clampedX = Math.max(0, Math.min(width - 1, x));
      const clampedY = Math.max(0, Math.min(height - 1, y));

      const duplicate = intersections.some(
        (point) =>
          Math.abs(point.x - clampedX) < epsilon &&
          Math.abs(point.y - clampedY) < epsilon,
      );

      if (!duplicate) {
        intersections.push({
          x: clampedX,
          y: clampedY,
        });
      }
    }
  };

  if (Math.abs(b) > 1e-12) {
    addPoint(0, -c / b);
  }

  if (Math.abs(b) > 1e-12) {
    addPoint(width - 1, -(a * (width - 1) + c) / b);
  }

  if (Math.abs(a) > 1e-12) {
    addPoint(-c / a, 0);
  }

  if (Math.abs(a) > 1e-12) {
    addPoint(-(b * (height - 1) + c) / a, height - 1);
  }

  if (intersections.length < 2) {
    return null;
  }

  let bestStart = intersections[0];
  let bestEnd = intersections[1];
  let bestDistanceSquared = -Infinity;

  for (let i = 0; i < intersections.length; i += 1) {
    for (let j = i + 1; j < intersections.length; j += 1) {
      const dx = intersections[j].x - intersections[i].x;
      const dy = intersections[j].y - intersections[i].y;
      const distanceSquared = dx * dx + dy * dy;

      if (distanceSquared > bestDistanceSquared) {
        bestDistanceSquared = distanceSquared;
        bestStart = intersections[i];
        bestEnd = intersections[j];
      }
    }
  }

  return {
    start: bestStart,
    end: bestEnd,
  };
}

// Patch matching
function extractPatch(gray, width, height, x, y, radius) {
  const centerX = Math.round(x);
  const centerY = Math.round(y);

  if (
    centerX - radius < 0 ||
    centerX + radius >= width ||
    centerY - radius < 0 ||
    centerY + radius >= height
  ) {
    return null;
  }

  const patch = [];

  for (let dy = -radius; dy <= radius; dy += 1) {
    for (let dx = -radius; dx <= radius; dx += 1) {
      const pixelX = centerX + dx;
      const pixelY = centerY + dy;

      patch.push(gray[pixelY * width + pixelX]);
    }
  }

  return patch;
}

function zncc(patchA, patchB) {
  if (
    !patchA ||
    !patchB ||
    patchA.length !== patchB.length ||
    patchA.length === 0
  ) {
    return -1;
  }

  let meanA = 0;
  let meanB = 0;

  for (let i = 0; i < patchA.length; i += 1) {
    meanA += patchA[i];
    meanB += patchB[i];
  }

  meanA /= patchA.length;
  meanB /= patchB.length;

  let numerator = 0;
  let varianceA = 0;
  let varianceB = 0;

  for (let i = 0; i < patchA.length; i += 1) {
    const centeredA = patchA[i] - meanA;
    const centeredB = patchB[i] - meanB;

    numerator += centeredA * centeredB;
    varianceA += centeredA * centeredA;
    varianceB += centeredB * centeredB;
  }

  const denominator = Math.sqrt(varianceA * varianceB);

  if (denominator < 1e-12) {
    return -1;
  }

  return numerator / denominator;
}

function sampleLineSegment(start, end, step = 1) {
  const dx = end.x - start.x;
  const dy = end.y - start.y;
  const length = Math.sqrt(dx * dx + dy * dy);

  if (length < 1e-12) {
    return [];
  }

  const count = Math.max(1, Math.ceil(length / step));
  const points = [];

  for (let i = 0; i <= count; i += 1) {
    const t = i / count;

    points.push({
      x: start.x + dx * t,
      y: start.y + dy * t,
    });
  }

  return points;
}

function findBestPatchMatch(
  sourcePatch,
  targetGray,
  targetWidth,
  targetHeight,
  line,
  { patchRadius, searchStep },
) {
  const segment = clipEpipolarLineToImage(line, targetWidth, targetHeight);

  if (!segment) {
    return null;
  }

  const searchPoints = sampleLineSegment(
    segment.start,
    segment.end,
    searchStep,
  );

  let bestScore = -Infinity;
  let secondBestScore = -Infinity;
  let bestPoint = null;

  for (const point of searchPoints) {
    const targetPatch = extractPatch(
      targetGray,
      targetWidth,
      targetHeight,
      point.x,
      point.y,
      patchRadius,
    );

    if (!targetPatch) {
      continue;
    }

    const score = zncc(sourcePatch, targetPatch);

    if (score > bestScore) {
      secondBestScore = bestScore;
      bestScore = score;
      bestPoint = point;
    } else if (score > secondBestScore) {
      secondBestScore = score;
    }
  }

  if (!bestPoint) {
    return null;
  }

  return {
    point: bestPoint,
    score: bestScore,
    secondBestScore,
  };
}

// Forward epipolar matching
export async function matchEpipolarCandidates(
  imageElementA,
  imageElementB,
  candidates,
  F,
  {
    patchRadius = 4,
    searchStep = 2,
    minimumScore = 0.8,
    ambiguityMargin = 0.05,
  } = {},
) {
  const cv = await getOpenCV();
  const sourceA = cv.imread(imageElementA);
  const sourceB = cv.imread(imageElementB);
  const grayA = new cv.Mat();
  const grayB = new cv.Mat();

  try {
    cv.cvtColor(sourceA, grayA, cv.COLOR_RGBA2GRAY);
    cv.cvtColor(sourceB, grayB, cv.COLOR_RGBA2GRAY);

    const widthA = grayA.cols;
    const heightA = grayA.rows;
    const widthB = grayB.cols;
    const heightB = grayB.rows;
    const dataA = grayA.data;
    const dataB = grayB.data;
    const matches = [];

    for (const pointA of candidates) {
      const patchA = extractPatch(
        dataA,
        widthA,
        heightA,
        pointA.x,
        pointA.y,
        patchRadius,
      );

      if (!patchA) {
        continue;
      }

      const line = computeEpipolarLine(F, pointA);

      if (!line) {
        continue;
      }

      const segment = clipEpipolarLineToImage(line, widthB, heightB);

      if (!segment) {
        continue;
      }

      const searchPoints = sampleLineSegment(
        segment.start,
        segment.end,
        searchStep,
      );

      let bestScore = -Infinity;
      let secondBestScore = -Infinity;
      let bestPoint = null;

      for (const pointB of searchPoints) {
        const patchB = extractPatch(
          dataB,
          widthB,
          heightB,
          pointB.x,
          pointB.y,
          patchRadius,
        );

        if (!patchB) {
          continue;
        }

        const score = zncc(patchA, patchB);

        if (score > bestScore) {
          secondBestScore = bestScore;
          bestScore = score;
          bestPoint = pointB;
        } else if (score > secondBestScore) {
          secondBestScore = score;
        }
      }

      if (!bestPoint) {
        continue;
      }

      if (bestScore < minimumScore) {
        continue;
      }

      if (
        Number.isFinite(secondBestScore) &&
        bestScore - secondBestScore < ambiguityMargin
      ) {
        continue;
      }

      matches.push({
        pointA: {
          x: pointA.x,
          y: pointA.y,
        },
        pointB: {
          x: bestPoint.x,
          y: bestPoint.y,
        },
        score: bestScore,
        secondBestScore,
      });
    }

    debugLog("[Reconstruction] Epipolar patch matching:", {
      candidates: candidates.length,
      matches: matches.length,
    });

    return matches;
  } finally {
    grayA.delete();
    grayB.delete();
    sourceA.delete();
    sourceB.delete();
  }
}

// Bidirectional consistency
export async function filterMatchesByBidirectionalConsistency(
  imageElementA,
  imageElementB,
  matches,
  F,
  { patchRadius = 4, searchStep = 2, maximumReturnDistance = 5 } = {},
) {
  const cv = await getOpenCV();
  const sourceA = cv.imread(imageElementA);
  const sourceB = cv.imread(imageElementB);
  const grayA = new cv.Mat();
  const grayB = new cv.Mat();

  try {
    cv.cvtColor(sourceA, grayA, cv.COLOR_RGBA2GRAY);
    cv.cvtColor(sourceB, grayB, cv.COLOR_RGBA2GRAY);

    const dataA = grayA.data;
    const dataB = grayB.data;
    const accepted = [];
    const rejected = [];

    for (const match of matches) {
      const patchB = extractPatch(
        dataB,
        grayB.cols,
        grayB.rows,
        match.pointB.x,
        match.pointB.y,
        patchRadius,
      );

      if (!patchB) {
        rejected.push(match);
        continue;
      }

      const reverseLine = computeReverseEpipolarLine(F, match.pointB);

      if (!reverseLine) {
        rejected.push(match);
        continue;
      }

      const reverseMatch = findBestPatchMatch(
        patchB,
        dataA,
        grayA.cols,
        grayA.rows,
        reverseLine,
        {
          patchRadius,
          searchStep,
        },
      );

      if (!reverseMatch) {
        rejected.push(match);
        continue;
      }

      const dx = reverseMatch.point.x - match.pointA.x;
      const dy = reverseMatch.point.y - match.pointA.y;
      const returnDistance = Math.sqrt(dx * dx + dy * dy);

      const result = {
        ...match,
        reversePoint: reverseMatch.point,
        reverseScore: reverseMatch.score,
        returnDistance,
      };

      if (returnDistance <= maximumReturnDistance) {
        accepted.push(result);
      } else {
        rejected.push(result);
      }
    }

    debugLog("[Reconstruction] Bidirectional epipolar consistency:", {
      input: matches.length,
      accepted: accepted.length,
      rejected: rejected.length,
    });

    return {
      accepted,
      rejected,
    };
  } finally {
    grayA.delete();
    grayB.delete();
    sourceA.delete();
    sourceB.delete();
  }
}
